/**
 * Web Vitals reporting.
 * monitoringSKILL Rule 8 — sends to PostHog (trends) and Sentry warning (when "poor").
 *
 * Budgets (Core Web Vitals 2024):
 *   LCP  < 2.5s (good) / < 4.0s (needs improvement) / >= 4.0s (poor)
 *   INP  < 200ms       / < 500ms                    / >= 500ms (replaces FID)
 *   CLS  < 0.1         / < 0.25                     / >= 0.25
 *   TTFB < 800ms       / < 1800ms                   / >= 1800ms
 *   FCP  < 1.8s        / < 3.0s                     / >= 3.0s
 *
 * Note: FID was deprecated in favour of INP (web-vitals@v4+, March 2024 CWV update).
 * Call initVitals() in main.tsx AFTER the router mounts for correct LCP attribution.
 */
import { onCLS, onFCP, onINP, onLCP, onTTFB, type Metric } from 'web-vitals'
import * as Sentry from '@sentry/react'
import { safeCapture } from '@/lib/analytics'

// "poor" thresholds — Sentry warning fires when these are exceeded
const POOR_BUDGETS: Record<string, number> = {
  LCP:  4000,
  INP:  500,
  CLS:  0.25,
  TTFB: 1800,
  FCP:  3000,
}

function sendVital({ name, value, rating }: Metric): void {
  // Always send to PostHog for trend dashboards.
  // COPPA: safeCapture() is a no-op when role === 'student' — web vitals must
  // never reach PostHog for child sessions. Never use posthog.capture() here.
  safeCapture('web_vital', {
    metric: name,
    // CLS: store as 1000x integer for precision (e.g. 0.123 → 123)
    value:  Math.round(name === 'CLS' ? value * 1000 : value),
    rating, // "good" | "needs-improvement" | "poor"
  })

  // Rule 8 — Sentry warning on "poor" rating
  if (rating === 'poor') {
    Sentry.captureMessage(
      `Poor Web Vital: ${name} = ${Math.round(value)} (budget: ${POOR_BUDGETS[name] ?? '?'})`,
      'warning'
    )
    Sentry.setTag(`vital_${name.toLowerCase()}_poor`, true)
  }
}

export function initVitals(): void {
  onCLS(sendVital)
  onFCP(sendVital)
  onINP(sendVital)   // replaces onFID (deprecated in web-vitals v4+)
  onLCP(sendVital)
  onTTFB(sendVital)
}
